import type { BrokerCommunication, RelatedEntityType } from "@dune/types";
import { useEffect, useRef } from "react";
import { useMe } from "../../api/queries/users.queries";
import {
	useEmailActions,
	useEmailComposerMode,
	useIsEmailComposerOpen,
	useSelectedEmailId,
} from "../../stores/email.store";
import type { EntityEmailConfig } from "../../types/panel.types";
import { createEmailActionsComponent } from "../../utils/email-actions.utils";
import { getEntityPanelComponent } from "../../utils/panel-registry";
import { usePanel } from "../usePanel";
import { RoleEnum } from "../../../../../packages/vars/src";

interface UseEmailSelectionProps {
	entityType: RelatedEntityType;
	entityId: number;
	communications: BrokerCommunication[];
	emailConfig?: EntityEmailConfig;
	customComponent?: React.ComponentType<
		import("../../types/panel.types").EmailPreviewPanelProps
	>;
	emailRoutingUuid?: string | null;
}

export const useEmailSelection = ({
	entityType,
	entityId,
	communications,
	emailConfig,
	customComponent,
	emailRoutingUuid,
}: UseEmailSelectionProps) => {
	const panel = usePanel();
	const { me } = useMe();
	const selectedEmailId = useSelectedEmailId();
	const { clearEmailSelection } = useEmailActions();
	const isComposerOpen = useIsEmailComposerOpen();
	const composerMode = useEmailComposerMode();
	const canCompose = me?.role === RoleEnum.DUNE;

	const emailConfigRef = useRef(emailConfig);
	const communicationsRef = useRef(communications);
	const customComponentRef = useRef(customComponent);

	emailConfigRef.current = emailConfig;
	communicationsRef.current = communications;
	customComponentRef.current = customComponent;

	const EmailPreviewComponent =
		customComponentRef.current ||
		getEntityPanelComponent(entityType, "EmailPreview");

	useEffect(() => {
		if (!selectedEmailId || !communications.length) return;

		if (isComposerOpen && composerMode.draftId === selectedEmailId) {
			return;
		}

		const mail = communications.find(
			(comm) => comm.id.toString() === selectedEmailId,
		);

		if (!mail) {
			clearEmailSelection();
			return;
		}

		const signature = me?.emailSignature || "";

		const getReplyToEmail =
			emailConfig?.getReplyToEmail ||
			((id: number) => `entity-${id}@example.com`);

		const replyToEmail = getReplyToEmail(entityId, emailRoutingUuid);

		const emailActions = createEmailActionsComponent({
			mail,
			entityId,
			entityType,
			replyToEmail,
			signature,
			showEmailComposer: canCompose ? panel.showEmailComposer : () => {},
			enabled: canCompose,
		});

		if (EmailPreviewComponent) {
			panel.showCustomContent({
				id: `email-preview-${mail.id}`,
				component: (
					<EmailPreviewComponent
						entityId={entityId}
						entityType={entityType}
						mail={mail}
						actions={emailActions}
						onImportEmail={emailConfig?.importEmailAttachmentsHandler}
					/>
				),
				meta: {
					title: mail.subject,
					actions: emailActions,
				},
			});
		} else {
			panel.showEmailPreview({
				mail,
				brokerId: entityId,
				actions: emailActions,
				onImportEmail: emailConfig?.importEmailAttachmentsHandler
					? (emailAttachmentId: number) => {
							emailConfig?.importEmailAttachmentsHandler?.(
								emailAttachmentId,
								entityId,
							);
						}
					: undefined,
			});
		}
	}, [
		selectedEmailId,
		entityId,
		entityType,
		emailRoutingUuid,
		communications,
		emailConfig,
		panel,
		me?.emailSignature,
		EmailPreviewComponent,
		clearEmailSelection,
		isComposerOpen,
		composerMode,
		canCompose,
	]);

	return {
		selectedEmailId,
		clearEmailSelection,
	};
};
